import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Shield, Users, Heart, DollarSign, Target, CheckCircle } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

export default function InvestorMarket() {
  const { t } = useLang();
  const inv = t.investorMarket;

  return (
    <section id="investor-market" className="py-32 md:py-40 relative overflow-hidden" style={{ background: '#0F1115' }}>
      <div
        className="orb-purple absolute"
        style={{ width: 700, height: 700, top: '-20%', left: '-10%', opacity: 0.08 }}
      />
      <div
        className="orb-gold absolute"
        style={{ width: 600, height: 600, bottom: '-15%', right: '-10%', opacity: 0.1 }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mb-20"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-bold">Market</span>
          </div>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 font-heading leading-[0.95]" style={{ fontFamily: "'Sora', sans-serif", textShadow: '0 2px 30px rgba(0,0,0,0.5)' }}>
            {inv.title}
          </h2>
          <p className="text-[#BFC3C9] text-xl md:text-2xl">{inv.subtitle}</p>
        </motion.div>

        {/* Main Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {inv.reasons.map((reason, i) => {
            const icons = [TrendingUp, Users, Heart, Target];
            const Icon = icons[i] || TrendingUp;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="glass-premium rounded-3xl p-8 card-premium group"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)', 
                    border: '1px solid rgba(212,175,55,0.25)',
                    boxShadow: '0 8px 24px rgba(212,175,55,0.1)'
                  }}
                >
                  <Icon size={24} className="text-[#D4AF37]" />
                </div>
                <p className="text-white text-lg font-semibold leading-relaxed">{reason}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Anti-Crisis Block */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mb-12"
        >
          <div
            className="rounded-[2.5rem] p-10 md:p-14 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(107,78,255,0.08) 0%, rgba(107,78,255,0.02) 100%)',
              border: '1px solid rgba(107,78,255,0.2)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
            }}
          >
            <div
              className="absolute inset-0 opacity-50"
              style={{ 
                background: 'radial-gradient(circle at 30% 20%, rgba(107,78,255,0.15) 0%, transparent 50%)'
              }}
            />

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <Shield size={32} className="text-[#9B7FFF]" />
                <h3 className="text-3xl md:text-4xl font-black text-white font-heading">{inv.antiCrisis.title}</h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* People reduce */}
                <div>
                  <p className="text-[#9B7FFF] text-sm uppercase tracking-widest font-bold mb-6">{inv.antiCrisis.reduce}</p>
                  <ul className="space-y-4">
                    {inv.antiCrisis.reduceList.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#BFC3C9] text-base">
                        <span className="text-red-400 mt-1">×</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* People continue */}
                <div>
                  <p className="text-[#22C55E] text-sm uppercase tracking-widest font-bold mb-6">{inv.antiCrisis.continue}</p>
                  <ul className="space-y-4">
                    {inv.antiCrisis.continueList.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#E5E7EB] text-base">
                        <CheckCircle size={20} className="text-[#22C55E] flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Investor Gets Highlight */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center"
        >
          <div
            className="inline-block rounded-[2rem] px-12 py-10 relative overflow-hidden max-w-4xl"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.04) 100%)',
              border: '1px solid rgba(212,175,55,0.3)',
              boxShadow: '0 20px 60px rgba(212,175,55,0.2)'
            }}
          >
            <div
              className="absolute inset-0"
              style={{ 
                background: 'radial-gradient(circle at 50% 0%, rgba(212,175,55,0.2) 0%, transparent 70%)'
              }}
            />

            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-6">
                <DollarSign size={28} className="text-[#D4AF37]" />
                <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-black">{inv.investorGets.title}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {inv.investorGets.list.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#D4AF37] flex-shrink-0 mt-1" />
                    <span className="text-white text-sm font-medium text-left">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
